import React, { useState } from 'react'
import { isFragment } from 'react-is'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import { ClassesType } from '../../types'
import PuiCloseButton from '../buttons/PuiCloseButton'
import Text from '../Text'
import ConfirmCloseDialog, {
  ConfirmCloseDialogProps,
} from './ConfirmCloseDialog'

const useStyles = makeStyles(
  theme => ({
    container: {
      backdropFilter: 'blur(8px)',
      '-webkit-backdrop-filter': 'blur(8px)',
    },
    paper: {},
    paperScrollBody: {
      // do not remove this
    },
    paperWidthSm: {
      '&$paperScrollBody': {
        [theme.breakpoints.down('sm')]: {
          margin: 16,
        },
      },
    },
    dialogContentRoot: {
      padding: 0,
      '&:first-child': {
        paddingTop: 0,
      },
    },
    title: {},
    dialogTitle: {
      padding: theme.spacing(1.5, 2),
      borderBottom: theme.constants.tableBorder,
      backgroundColor: 'inherit',
      zIndex: theme.utils.modifyZIndex(theme.zIndex.modal, 'above'),
    },
    dialogTitleWithExitButton: {
      paddingRight: theme.spacing(5),
    },
    closeButton: {
      zIndex: theme.utils.modifyZIndex(theme.zIndex.modal, 'above', 2),
    },
    actions: {
      padding: theme.spacing(1, 2),
      borderTop: theme.constants.tableBorder,
      justifyContent: 'flex-start',
    },
  }),
  { name: 'PuiDialog' },
)

export interface BasePuiDialogProps extends Pick<DialogProps, 'open'> {
  readonly onClose?: (event?: any) => void
}

export interface PuiDialogProps
  extends BasePuiDialogProps,
  Omit<DialogProps, 'title'> {
  readonly ConfirmCloseDialogProps?: Partial<ConfirmCloseDialogProps>
  readonly actions?: React.ReactNode
  readonly classes?: ClassesType<typeof useStyles>
  readonly confirmSaveOnClose?: boolean
  readonly disableClose?: boolean
  readonly hasUnsavedChanges?: () => boolean
  readonly header?: React.ReactNode
  readonly hideCloseButton?: boolean
  onClose?: (event?: any) => void
  readonly title?: React.ReactNode
}

interface UseStylesProps extends DialogProps {
  classes?: ClassesType<typeof useStyles>
}

const ConfirmCloseDialogPropsDefault = {}

const PuiDialog = ({
  children,
  onClose,
  hideCloseButton = false,
  disableClose = false,
  classes: classesProp,
  title,
  header,
  actions,
  'aria-labelledby': ariaLabelledby,
  confirmSaveOnClose = false,
  hasUnsavedChanges,
  ConfirmCloseDialogProps: {
    onOk: onOkAction,
    ...restConfirmCloseDialogProps
  } = ConfirmCloseDialogPropsDefault,
  ...rest
}: PuiDialogProps) => {
  const useStylesProps: UseStylesProps = {
    classes: classesProp,
    ...rest,
  }

  const classes = useStyles(useStylesProps)
  const hasActions = isFragment(actions)
    ? (actions.props.children || []).some(Boolean)
    : Boolean(actions)

  const [saveChangesAlertOpen, setSaveChangesAlertOpen] = useState(false)

  const handleClose = (event: any) => {
    if (disableClose) {
      return
    }

    if (
      confirmSaveOnClose &&
      hasUnsavedChanges &&
      typeof hasUnsavedChanges === 'function' &&
      hasUnsavedChanges()
    ) {
      setSaveChangesAlertOpen(true)
    } else if (onClose) {
      onClose(event)
    }
  }

  const checkReasonAndClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick') {
      handleClose(event)
    }
  }

  return (
    <>
      <Dialog
        aria-labelledby={ariaLabelledby}
        classes={{
          container: classes.container,
          paper: classes.paper,
          paperScrollBody: classes.paperScrollBody,
          paperWidthSm: classes.paperWidthSm,
        }}
        scroll="body"
        onClose={checkReasonAndClose}
        {...rest}
      >
        {(title || header) && (
          <DialogTitle
            className={classNames(
              classes.dialogTitle,
              !hideCloseButton && classes.dialogTitleWithExitButton,
            )}
            id={ariaLabelledby}
          >
            {title && (
              <Text className={classes.title} variant="h2">
                {title}
              </Text>
            )}
            {header}
          </DialogTitle>
        )}
        {!hideCloseButton && (
          <PuiCloseButton
            classes={{ iconButton: classes.closeButton }}
            disabled={disableClose}
            onClick={handleClose}
          />
        )}
        <DialogContent className={classes.dialogContentRoot}>
          {children}
        </DialogContent>
        {hasActions && (
          <DialogActions className={classes.actions}>{actions}</DialogActions>
        )}
      </Dialog>
      {confirmSaveOnClose && (
        <ConfirmCloseDialog
          open={saveChangesAlertOpen}
          onClose={() => setSaveChangesAlertOpen(false)}
          onNotOk={() => {
            setSaveChangesAlertOpen(false)
            if (onClose) {
              onClose()
            }
          }}
          onOk={() => {
            setSaveChangesAlertOpen(false)
            if (onOkAction) {
              onOkAction()
            }
          }}
          {...restConfirmCloseDialogProps}
        />
      )}
    </>
  )
}

export default PuiDialog
